<script setup lang="ts">
import { usersByUidRef } from '@/services/users';
import { dateLocal, dateToLocaleDateString } from '../../../compliance-shared/services/dates-and-durations';
import type { Role, Task } from '../../../compliance-shared/types/compliance';
import { computed, type PropType } from 'vue';
import type { Uid } from '../../../cross-app-shared/types/common';
import TextTruncate from '@/components/util/TextTruncate.vue';

const props = defineProps({
  task: {
    type: Object as PropType<Task>,
    required: true,
  },
  roles: {
    type: Array as PropType<Role[] | null>,
    required: true,
  },
  showTitle: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const owner = computed(() => {
  if (props.task.ownerConfigured.startsWith('role_')) {
    const role = props.roles?.find((r) => r.roleID === props.task.ownerConfigured);
    return role?.name || 'some role';
  }

  const user = usersByUidRef.value.get(props.task.owner);
  return user?.name || user?.email || 'someone';
});

const involved = computed(() => {
  if (props.task?.involved.length === 0) {
    return 'No one else';
  }

  let configured = props.task.involvedConfigured;
  let ownerSpilledOver = false;
  if (props.task.ownerConfigured.startsWith('role_')) {
    const role = props.roles?.find((role) => role.roleID === props.task.ownerConfigured);
    if (role && role.involved.length > 1) {
      configured = [props.task.ownerConfigured].concat(configured);
      ownerSpilledOver = true;
    }
  }

  return configured
    .filter((uid) => !ownerSpilledOver || uid !== props.task.owner)
    .map((i) => {
      if (i.startsWith('role_')) {
        const role = props.roles?.find((r) => r.roleID === i);
        return role?.name || 'some role';
      }

      const user = usersByUidRef.value.get(i as Uid);
      return user?.name || user?.email || 'someone';
    })
    .join(', ');
});
</script>

<template>
  <div
    class="flex items-start gap-3 rounded-md border border-slate-200 bg-white px-3 py-2"
    :style="task.deletedAt ? 'opacity: 0.5;' : ''"
    v-if="task"
  >
    <div class="flex shrink-0 flex-col gap-1" style="min-width: 150px; max-width: 150px">
      <span
        v-if="task.deletedAt"
        class="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"
        >Deleted!</span
      >
      <span
        v-if="task.completedAt"
        class="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"
        >Completed</span
      >
      <span
        v-if="!task.deletedAt && !task.completedAt && task.overdue"
        class="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
        >Overdue</span
      >
      <strong class="text-sm text-slate-800">Due {{ dateToLocaleDateString(task.dueAt) }}</strong>
      <strong v-if="task.dueAtOrig && task.dueAtOrig !== task.dueAt" class="text-sm text-slate-700">
        (Originally due {{ dateToLocaleDateString(task.dueAtOrig) }})
      </strong>
      <strong v-if="task.completedAt" class="text-sm text-slate-700"
        >Completed {{ dateToLocaleDateString(dateLocal(task.completedAt)) }}</strong
      >
    </div>

    <div class="flex min-w-0 flex-col gap-1 overflow-hidden" style="overflow-wrap: break-word">
      <span v-if="showTitle" class="text-sm text-slate-800">Title: {{ task.title }}</span>
      <span class="text-sm text-slate-500">Owner: {{ owner }}</span>
      <span class="text-sm text-slate-500">Involved: {{ involved }}</span>
      <span class="text-sm text-slate-500" v-if="task.description">
        <TextTruncate :text="'Description: ' + task.description" :maxLength="200" />
      </span>
    </div>
  </div>

  <div class="rounded-md border border-slate-200 bg-white p-1 text-center text-lg text-slate-500" v-else>
    Empty Task
  </div>
</template>
